import React, { useState } from "react";
import {
  Box,
  Center,
  FormControl,
  FormLabel,
  Link,
  Text,
  VStack,
  FormErrorMessage,
  Spinner,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomInput from "../../components/UI/CustomInput";
import CustomButton from "../../components/UI/CustomButton";
import { validateEmail } from "../../helpers/utils";
import { motion } from "framer-motion";
import { useFormTheme } from "../../hooks/useFormTheme";
import AuthLogo from "../../components/UI/AuthLogo";
import { useAuth } from "../../context/AuthContext";
import FormErrorBox from "../../components/UI/FormErrorBox";
import { USER_EMAIL_KEY } from "../../helpers/constant";
import { useTranslation } from "react-i18next";
import {
  getAuthErrorCode,
  getLocalizedAuthErrorMessage,
} from "../../helpers/authErrors";

const MotionBox = motion(Box);

const authCardTransition = {
  layout: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
};

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { t } = useTranslation();
  const {
    signInHandler,
    signOutHandler,
    resendVerificationCodeHandler,
    isLoading,
  } = useAuth();

  const validate = (
    nextEmail: string = email,
    nextPassword: string = password,
  ): boolean => {
    const newErrors: { email?: string; password?: string } = {};

    if (!nextEmail) {
      newErrors.email = String(t("auth.common.emailRequired"));
    } else if (!validateEmail(nextEmail)) {
      newErrors.email = String(t("auth.common.invalidEmail"));
    }

    if (!nextPassword) {
      newErrors.password = String(t("auth.common.passwordRequired"));
    } else if (nextPassword.length < 6) {
      newErrors.password = String(t("auth.common.passwordTooShort"));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const profile = await signInHandler(email, password);

      if (
        profile.status === "awaiting_validation" ||
        profile.status === "pending_validation"
      ) {
        toast({
          status: "info",
          title: t("auth.login.pendingTitle"),
          description: t("auth.login.pendingDescription"),
          duration: 4000,
          isClosable: true,
          position: "top",
        });
        navigate("/authentication-pending", { replace: true });
        return;
      }

      if (profile.status === "blocked") {
        await signOutHandler();
        toast({
          title: t("auth.login.blockedTitle"),
          description: t("auth.login.blockedDescription"),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
        navigate("/login", { replace: true });
        return;
      }

      toast({
        status: "success",
        title: t("auth.login.successTitle"),
        description: t("auth.login.successDescription"),
        duration: 2000,
        isClosable: true,
        position: "top",
      });

      const origin = new URLSearchParams(location.search).get("origin");
      if (origin === "admin") {
        return;
      }

      const redirectTo =
        (location.state as { from?: string } | null)?.from ?? "/";
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const errorCode = getAuthErrorCode(error);

      if (errorCode === "UserNotConfirmedException") {
        let description = t("auth.login.unverifiedDefault");
        try {
          setIsResendingCode(true);
          await resendVerificationCodeHandler(email);
        } catch (resendError) {
          description =
            getLocalizedAuthErrorMessage(resendError, t) ||
            String(t("auth.login.unverifiedFallback"));
        } finally {
          setIsResendingCode(false);
        }

        localStorage.setItem(USER_EMAIL_KEY, email);
        toast({
          title: t("auth.login.unverifiedTitle"),
          description,
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
        navigate("/verify-code", {
          state: {
            email,
          },
        });
        return;
      }

      toast({
        title: t("auth.login.errorTitle"),
        description: getLocalizedAuthErrorMessage(error, t),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  const {
    bg,
    errorColor,
    linkColor,
    pageBg,
    textColor,
    lightGradient,
    darkGradient,
  } = useFormTheme();

  return (
    <Center
      h="100vh"
      w="full"
      bg={pageBg}
      bgGradient={lightGradient}
      _dark={{
        bgGradient: darkGradient,
      }}
    >
      <MotionBox
        maxW={{ base: "90%", md: "400px", "2xl": "450px" }}
        px={{ base: "24px", md: "40px" }}
        w="full"
        bg={bg}
        borderRadius="4px"
        boxShadow="rgba(0, 0, 0, 0.1) 0px 0px 10px"
        py="44px"
        layout
        transition={authCardTransition}
      >
        <AuthLogo />

        <Center mb={6}>
          <Text fontSize="16px" fontWeight="medium" color={textColor}>
            {t("auth.login.title")}
          </Text>
        </Center>

        <form onSubmit={handleSubmit} autoComplete="off">
          <VStack spacing={{ base: "24px", md: "32px" }} align="stretch">
            <FormControl isInvalid={!!errors.email}>
              <FormLabel fontSize="14px" fontWeight="medium" color={textColor}>
                {t("auth.login.email")}
              </FormLabel>
              <CustomInput
                type="email"
                name="email"
                placeholder={String(t("auth.login.emailPlaceholder"))}
                value={email}
                error={!!errors.email}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);
                  if (hasSubmitted) {
                    validate(value, password);
                  }
                }}
              />
              <FormErrorMessage color={errorColor}>
                {errors.email}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.password}>
              <FormLabel fontSize="14px" fontWeight="medium" color={textColor}>
                {t("auth.login.password")}
              </FormLabel>
              <CustomInput
                type="password"
                name="password"
                error={!!errors.password}
                placeholder={String(t("auth.login.passwordPlaceholder"))}
                value={password}
                onChange={(e) => {
                  const value = e.target.value;
                  setPassword(value);
                  if (hasSubmitted) {
                    validate(email, value);
                  }
                }}
              />
              <FormErrorMessage color={errorColor}>
                {errors.password}
              </FormErrorMessage>
            </FormControl>

            <FormErrorBox errors={Object.values(errors)} />

            <CustomButton
              type="submit"
              isDisabled={loading || isLoading || isResendingCode}
            >
              {loading || isLoading || isResendingCode ? (
                <Spinner size="sm" />
              ) : (
                t("auth.login.submit")
              )}
            </CustomButton>

            <Center mt={4}>
              <HStack spacing={2} w="full" justifyContent="space-between">
                <Link
                  color={linkColor}
                  textDecor="underline"
                  fontSize="14px"
                  textAlign="center"
                  onClick={() => navigate("/forgot-password")}
                >
                  {t("auth.login.forgotPassword")}
                </Link>
                <Text color={textColor}>•</Text>
                <Link
                  color={linkColor}
                  textDecor="underline"
                  fontSize="14px"
                  textAlign="center"
                  onClick={() => navigate("/register")}
                >
                  {t("auth.login.createAccount")}
                </Link>
              </HStack>
            </Center>
          </VStack>
        </form>
      </MotionBox>
    </Center>
  );
};

export default LoginPage;
